import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Clock3, Copy, Download, Info, Loader2, RefreshCw, Save } from 'lucide-react';
import {
  placementsApi,
  PlacementBatchSummary,
  PlacementBatchDetail,
  PlacementManageTrainee,
  PlacementBulkUpsertPayload,
  PlacementOverviewRow,
} from '../api';
import { useAuth } from '../hooks/useAuth';

const MANAGE_ROLES = ['SYSTEM_ADMIN', 'MOBILISATION_PARTNER_ADMIN', 'CENTER_ADMIN', 'PROJECT_MANAGER'];
const JOB_TYPE_OPTIONS = [
  { label: 'On Role', value: 'FullTime' },
  { label: 'Off Role', value: 'Contract' },
  { label: 'Internship', value: 'Internship' },
  { label: 'Part Time', value: 'PartTime' },
];

type TraineeTabKey = 'NEEDS' | 'RECORDED' | 'NOT_ELIGIBLE' | 'ALL';

const TRAINEE_TAB_LABELS: Record<TraineeTabKey, string> = {
  NEEDS: 'Needs Placement',
  RECORDED: 'Already Recorded',
  NOT_ELIGIBLE: 'Not Eligible',
  ALL: 'All Trainees',
};

interface PlacementFormState {
  company_name: string;
  job_role: string;
  job_type: string;
  offered_salary: string;
}

interface OriginalPlacementState extends PlacementFormState {
  hasPlacement: boolean;
}

const EMPTY_FORM: PlacementFormState = {
  company_name: '',
  job_role: '',
  job_type: '',
  offered_salary: '',
};

const parseError = (error: unknown) => {
  if ((error as any)?.statusCode === 403) {
    return 'You do not have permission to perform this action.';
  }
  if ((error as any)?.statusCode === 409) {
    return (error as any)?.message || 'Conflict while processing placements.';
  }
  if ((error as any)?.statusCode === 400) {
    return (error as any)?.message || 'Invalid data in request.';
  }
  if (error instanceof Error) return error.message;
  return 'Something went wrong. Please try again.';
};

const downloadBlob = (filename: string, blob: Blob) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const createFormState = (trainee: PlacementManageTrainee): PlacementFormState => ({
  company_name: trainee.placement?.company_name ?? '',
  job_role: trainee.placement?.job_role ?? '',
  job_type: trainee.placement?.job_type ?? '',
  offered_salary:
    trainee.placement?.offered_salary_value !== null && trainee.placement?.offered_salary_value !== undefined
      ? String(trainee.placement.offered_salary_value)
      : '',
});

const hasAnyValue = (form: PlacementFormState) =>
  form.company_name.trim() !== '' ||
  form.job_role.trim() !== '' ||
  form.job_type.trim() !== '' ||
  form.offered_salary.trim() !== '';

export default function PlacementsManagement() {
  const { user } = useAuth();
  const canManage = !!user && MANAGE_ROLES.includes(user.role);
  const isTrainee = user?.role === 'TRAINEE';

  const [batchSummaries, setBatchSummaries] = useState<PlacementBatchSummary[]>([]);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [listError, setListError] = useState<string | null>(null);

  const [selectedDetail, setSelectedDetail] = useState<PlacementBatchDetail | null>(null);
  const [trainees, setTrainees] = useState<PlacementManageTrainee[]>([]);
  const [formState, setFormState] = useState<Record<number, PlacementFormState>>({});
  const [originalState, setOriginalState] = useState<Record<number, OriginalPlacementState>>({});
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [markingPostPlacement, setMarkingPostPlacement] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TraineeTabKey>('NEEDS');
  const [bulkValues, setBulkValues] = useState<PlacementFormState>({ ...EMPTY_FORM });
  const [skippedTrainees, setSkippedTrainees] = useState<Set<number>>(new Set());

  const [traineePlacements, setTraineePlacements] = useState<PlacementOverviewRow[]>([]);
  const [traineeLoading, setTraineeLoading] = useState<boolean>(false);
  const [traineeError, setTraineeError] = useState<string | null>(null);
  const loadBatchSummaries = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const response = await placementsApi.listManageBatches();
      const batches = response.data?.data?.batches ?? [];
      setBatchSummaries(batches);
    } catch (err) {
      setListError(parseError(err));
    } finally {
      setListLoading(false);
    }
  }, []);

  const initializeBatchDetail = useCallback((detail: PlacementBatchDetail) => {
    const nextForm: Record<number, PlacementFormState> = {};
    const nextOriginal: Record<number, OriginalPlacementState> = {};
    detail.trainees.forEach((trainee) => {
      const form = createFormState(trainee);
      nextForm[trainee.trainee_id] = form;
      nextOriginal[trainee.trainee_id] = { ...form, hasPlacement: !!trainee.placement };
    });
    setFormState(nextForm);
    setOriginalState(nextOriginal);
    setTrainees(detail.trainees);
    setSelectedDetail(detail);
    setActiveTab('NEEDS');
    setBulkValues({ ...EMPTY_FORM });
    setSkippedTrainees(new Set());
    setError(null);
  }, []);

  const loadBatchDetail = useCallback(
    async (batchId: number) => {
      setDetailLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const response = await placementsApi.getBatchDetail(batchId);
        const detail = response.data?.data;
        if (!detail) {
          setError('Unable to load batch placements.');
          return;
        }
        initializeBatchDetail(detail);
      } catch (err) {
        setError(parseError(err));
      } finally {
        setDetailLoading(false);
      }
    },
    [initializeBatchDetail]
  );

  useEffect(() => {
    if (canManage) {
      loadBatchSummaries();
    }
  }, [canManage, loadBatchSummaries]);

  useEffect(() => {
    if (isTrainee && !canManage) {
      const run = async () => {
        setTraineeLoading(true);
        setTraineeError(null);
        try {
          const response = await placementsApi.listMine();
          const rows = response.data ? response.data?.data?.rows : [];
          setTraineePlacements(rows as PlacementOverviewRow[]);
        } catch (err) {
          setTraineeError(parseError(err));
        } finally {
          setTraineeLoading(false);
        }
      };
      run();
    }
  }, [isTrainee, canManage]);

  const diff = useMemo(() => {
    const upserts: NonNullable<PlacementBulkUpsertPayload['upserts']> = [];
    const deletes: number[] = [];
    let changed = false;

    trainees.forEach((trainee) => {
      if (!trainee.post_assessment_done) return;
      if (skippedTrainees.has(trainee.trainee_id)) return;
      const current = formState[trainee.trainee_id] ?? EMPTY_FORM;
      const original = originalState[trainee.trainee_id] ?? { ...EMPTY_FORM, hasPlacement: false };

      const currentCompany = current.company_name.trim();
      const currentRole = current.job_role.trim();
      const currentJobType = current.job_type || '';
      const currentSalary = current.offered_salary.trim();

      const originalCompany = original.company_name.trim();
      const originalRole = original.job_role.trim();
      const originalJobType = original.job_type || '';
      const originalSalary = original.offered_salary.trim();

      const hasValues =
        currentCompany !== '' || currentRole !== '' || currentJobType !== '' || currentSalary !== '';

      if (original.hasPlacement) {
        if (!hasValues) {
          deletes.push(trainee.trainee_id);
          changed = true;
        } else if (
          currentCompany !== originalCompany ||
          currentRole !== originalRole ||
          currentJobType !== originalJobType ||
          currentSalary !== originalSalary
        ) {
          upserts.push({
            trainee_id: trainee.trainee_id,
            company_name: currentCompany || null,
            job_role: currentRole || null,
            job_type: currentJobType || null,
            offered_salary: currentSalary || null,
          });
          changed = true;
        }
      } else if (hasValues) {
        upserts.push({
          trainee_id: trainee.trainee_id,
          company_name: currentCompany || null,
          job_role: currentRole || null,
          job_type: currentJobType || null,
          offered_salary: currentSalary || null,
        });
        changed = true;
      }
    });

    return {
      upserts,
      deletes,
      changed,
    };
  }, [trainees, formState, originalState, skippedTrainees]);

  const handleSelectBatch = useCallback(
    (batch: PlacementBatchSummary) => {
      loadBatchDetail(batch.batch_id);
    },
    [loadBatchDetail]
  );

  const handleBackToBatches = useCallback(() => {
    setSelectedDetail(null);
    setTrainees([]);
    setFormState({});
    setOriginalState({});
    setActiveTab('NEEDS');
    setBulkValues({ ...EMPTY_FORM });
    setSkippedTrainees(new Set());
    setMarkingPostPlacement(false);
    setError(null);
    setSuccess(null);
  }, []);

  const handleFieldChange = useCallback(
    (traineeId: number, field: keyof PlacementFormState, value: string) => {
      setFormState((prev) => ({
        ...prev,
        [traineeId]: {
          ...(prev[traineeId] ?? { ...EMPTY_FORM }),
          [field]: value,
        },
      }));
    },
    []);

  const handleClearRow = useCallback((traineeId: number) => {
    if (!selectedDetail?.batch.can_edit) return;
    setFormState((prev) => ({
      ...prev,
      [traineeId]: { ...EMPTY_FORM },
    }));
    setSkippedTrainees((prev) => {
      if (!prev.size) return prev;
      if (!prev.has(traineeId)) return prev;
      const next = new Set(prev);
      next.delete(traineeId);
      return next;
    });
  }, [selectedDetail]);

  const handleBulkValueChange = useCallback(
    (field: keyof PlacementFormState, value: string) => {
      setBulkValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  const handleApplyBulkValues = useCallback(() => {
    if (!selectedDetail?.batch.can_edit) {
      setError('This batch is read-only. Bulk apply is disabled.');
      setSuccess(null);
      return;
    }

    const targetIds = trainees
      .filter(
        (trainee) =>
          trainee.post_assessment_done &&
          !trainee.has_placement &&
          !skippedTrainees.has(trainee.trainee_id)
      )
      .map((trainee) => trainee.trainee_id);

    if (!targetIds.length) {
      setError('No eligible trainees to apply bulk values.');
      setSuccess(null);
      return;
    }

    setFormState((prev) => {
      const next = { ...prev };
      targetIds.forEach((id) => {
        const current = next[id] ?? { ...EMPTY_FORM };
        next[id] = {
          ...current,
          company_name:
            bulkValues.company_name.trim() !== '' ? bulkValues.company_name : current.company_name,
          job_role:
            bulkValues.job_role.trim() !== '' ? bulkValues.job_role : current.job_role,
          job_type: bulkValues.job_type.trim() !== '' ? bulkValues.job_type : current.job_type,
          offered_salary:
            bulkValues.offered_salary.trim() !== ''
              ? bulkValues.offered_salary
              : current.offered_salary,
        };
      });
      return next;
    });
    setSuccess('Bulk values applied to pending trainees.');
    setError(null);
  }, [bulkValues, selectedDetail, setError, setSuccess, skippedTrainees, trainees]);

  const handleResetBulkValues = useCallback(() => {
    setBulkValues({ ...EMPTY_FORM });
  }, []);

  const handleMarkPostPlacement = useCallback(async () => {
    if (!selectedDetail) return;
    if (user?.role !== 'SYSTEM_ADMIN') {
      setError('Only system admins can mark a batch as post-placement.');
      setSuccess(null);
      return;
    }

    setMarkingPostPlacement(true);
    setError(null);
    try {
      const response = await placementsApi.markPostPlacement(selectedDetail.batch.batch_id);
      const detail = response.data?.data;
      if (!detail) {
        throw new Error('Unable to update batch status.');
      }
      initializeBatchDetail(detail);
      setSuccess('Batch moved to post-placement. Tracking schedule created.');
      await loadBatchSummaries();
    } catch (err) {
      setError(parseError(err));
      setSuccess(null);
    } finally {
      setMarkingPostPlacement(false);
    }
  }, [initializeBatchDetail, loadBatchSummaries, selectedDetail, setError, setSuccess, user]);

  const handleMarkRemainingLater = useCallback(() => {
    if (!selectedDetail?.batch.can_edit) {
      setError('This batch is read-only.');
      setSuccess(null);
      return;
    }

    let added = false;
    setSkippedTrainees((prev) => {
      const next = new Set(prev);
      trainees.forEach((trainee) => {
        if (!trainee.post_assessment_done || trainee.has_placement) return;
        const form = formState[trainee.trainee_id] ?? EMPTY_FORM;
        if (!hasAnyValue(form) && !next.has(trainee.trainee_id)) {
          next.add(trainee.trainee_id);
          added = true;
        }
      });
      return next;
    });
    if (added) {
      setSuccess('Marked unfilled trainees to revisit later.');
      setError(null);
    } else {
      setError('No unfilled eligible trainees to mark at the moment.');
      setSuccess(null);
    }
  }, [formState, selectedDetail, setError, setSuccess, trainees]);

  const handleToggleSkip = useCallback((traineeId: number) => {
    if (!selectedDetail?.batch.can_edit) return;
    setSkippedTrainees((prev) => {
      const next = new Set(prev);
      if (next.has(traineeId)) {
        next.delete(traineeId);
      } else {
        next.add(traineeId);
      }
      return next;
    });
  }, [selectedDetail]);

  const handleSavePlacements = useCallback(async () => {
    if (!selectedDetail) return;
    if (!selectedDetail.batch.can_edit) {
      setError('This batch is read-only. Placements cannot be modified.');
      setSuccess(null);
      return;
    }
    if (!diff.changed) {
      setSuccess('No changes to save.');
      return;
    }

    const invalidSalary = diff.upserts?.find((record) => {
      const value = record.offered_salary;
      if (value === null || value === undefined || value === '') return false;
      return Number.isNaN(Number(value));
    });

    if (invalidSalary) {
      setError('Salary must be a valid number.');
      return;
    }

    const payload: PlacementBulkUpsertPayload = {};
    if (diff.upserts.length) payload.upserts = diff.upserts;
    if (diff.deletes.length) payload.deletes = diff.deletes;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await placementsApi.saveBatchPlacements(selectedDetail.batch.batch_id, payload);
      const detail = response.data?.data;
      if (!detail) {
        setError('Unable to save placements.');
        return;
      }
      initializeBatchDetail(detail);
      setSuccess('Placements saved successfully.');
      setBulkValues({ ...EMPTY_FORM });
      setSkippedTrainees(new Set());
      setActiveTab('NEEDS');
      await loadBatchSummaries();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setSaving(false);
    }
  }, [diff, initializeBatchDetail, loadBatchSummaries, selectedDetail]);

  const handleExportBatch = useCallback(async (batchId: number, batchCode: string) => {
    try {
      const response = await placementsApi.exportCsv(batchId);
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      downloadBlob(`${batchCode}_placements.csv`, blob);
    } catch (err) {
      setError(parseError(err));
    }
  }, []);

  const traineeStats = useMemo(() => {
    const needs: PlacementManageTrainee[] = [];
    const recorded: PlacementManageTrainee[] = [];
    const notEligible: PlacementManageTrainee[] = [];

    trainees.forEach((trainee) => {
      if (!trainee.post_assessment_done) {
        notEligible.push(trainee);
      } else if (trainee.has_placement) {
        recorded.push(trainee);
      } else {
        needs.push(trainee);
      }
    });

    return {
      needs,
      recorded,
      notEligible,
      counts: {
        needs: needs.length,
        recorded: recorded.length,
        notEligible: notEligible.length,
        totalEligible: needs.length + recorded.length,
      },
    };
  }, [trainees]);

  const filteredTrainees = useMemo(() => {
    switch (activeTab) {
      case 'NEEDS':
        return traineeStats.needs;
      case 'RECORDED':
        return traineeStats.recorded;
      case 'NOT_ELIGIBLE':
        return traineeStats.notEligible;
      default:
        return trainees;
    }
  }, [activeTab, traineeStats, trainees]);

  const pendingCount = traineeStats.counts.needs;
  const recordedCount = traineeStats.counts.recorded;
  const notEligibleCount = traineeStats.counts.notEligible;
  const eligibleCount = traineeStats.counts.totalEligible;
  const renderBatchList = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Placements Management</h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            Select a batch to record or update placements for trainees who have completed their post assessment.
          </p>
        </div>
      </div>

      {listError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {listError}
        </div>
      )}

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Batch</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Program</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Center</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Trainees</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Placed</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Pending</th>
                {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Last Recorded</th> */}
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {listLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-primary-600">
                    <div className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading batches...
                    </div>
                  </td>
                </tr>
              )}

              {!listLoading && batchSummaries.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                    No batches available for placements.
                  </td>
                </tr>
              )}

              {!listLoading &&
                batchSummaries.map((batch) => (
                  <tr key={batch.batch_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="font-semibold text-gray-900">{batch.batch_code}</div>
                      <div className="text-xs text-gray-500">Status: {batch.status}</div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs">
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 font-medium text-blue-700">
                          {batch.placed_trainees}/{batch.eligible_trainees} placed
                        </span>
                        {/* <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${
                          batch.pending_placements > 0
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {batch.pending_placements} pending
                        </span> */}
                      </div>
                      {(() => {
                        if (batch.status === 'Completed') {
                          return (
                            <span className="mt-2 inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                              Placement completed
                            </span>
                          );
                        }
                        if (batch.ready_for_placements) {
                          return (
                            <span className="mt-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                              Ready for placements
                            </span>
                          );
                        }
                        // return (
                        //   <span className="mt-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                        //     Awaiting post assessment
                        //   </span>
                        // );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{batch.program_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div>{batch.center_name || '—'}</div>
                      <div className="text-xs text-gray-500">MP: {batch.mobilisation_partner_name || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{batch.eligible_trainees}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">{batch.placed_trainees}</td>
                    <td className="px-4 py-3 text-center text-sm text-gray-700">
                      <span className={batch.pending_placements > 0 ? 'text-amber-600 font-semibold' : 'text-green-600 font-semibold'}>
                        {batch.pending_placements}
                      </span>
                    </td>
                    {/* <td className="px-4 py-3 text-sm text-gray-700">
                      {batch.last_recorded_on ? new Date(batch.last_recorded_on).toLocaleString() : '—'}
                    </td> */}
                    <td className="px-4 py-3 text-right text-sm text-gray-700">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => handleExportBatch(batch.batch_id, batch.batch_code)}
                          className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          <Download className="h-3.5 w-3.5 mr-1" />
                        </button>
                        <button
                          onClick={() => handleSelectBatch(batch)}
                          className={`inline-flex items-center rounded-md px-3 py-1 text-xs font-medium ${
                            batch.can_edit
                              ? 'bg-primary-600 text-white hover:bg-primary-700'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          {batch.can_edit ? 'Manage' : 'View'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  const renderBatchDetail = () => {
    if (!selectedDetail) return null;

    const batch = selectedDetail.batch;
    const totalTrainees = trainees.length;
    const canEditBatch = batch.can_edit;
    const canMarkPostPlacement =
      user?.role === 'SYSTEM_ADMIN' &&
      !['PostPlacement', 'Completed', 'Cancelled'].includes(batch.status);

    const tabConfig: Array<{ key: TraineeTabKey; label: string; count: number }> = [
      { key: 'NEEDS', label: TRAINEE_TAB_LABELS.NEEDS, count: pendingCount },
      { key: 'RECORDED', label: TRAINEE_TAB_LABELS.RECORDED, count: recordedCount },
      { key: 'NOT_ELIGIBLE', label: TRAINEE_TAB_LABELS.NOT_ELIGIBLE, count: notEligibleCount },
      { key: 'ALL', label: TRAINEE_TAB_LABELS.ALL, count: totalTrainees },
    ];

    const emptyStateMessage = (() => {
      switch (activeTab) {
        case 'NEEDS':
          return 'No trainees currently need placement updates.';
        case 'RECORDED':
          return 'No placements have been recorded yet.';
        case 'NOT_ELIGIBLE':
          return 'All trainees have completed their post assessment.';
        default:
          return 'No trainees found.';
      }
    })();

    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <button
              onClick={handleBackToBatches}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to batches
            </button>
            <h2 className="mt-2 text-2xl font-semibold text-gray-900">Batch {batch.batch_code}</h2>
            <p className="text-sm text-gray-600">
              Program: {batch.program_name || '—'} • Center: {batch.center_name || '—'} • Trainer: {batch.trainer_name || '—'}
            </p>
            <p className="text-xs text-gray-500">Status: {batch.status}</p>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1"><strong className="font-semibold text-gray-700">Eligible:</strong> {eligibleCount}</span>
              <span className="inline-flex items-center gap-1"><strong className="font-semibold text-gray-700">Placed:</strong> {recordedCount}</span>
              <span className="inline-flex items-center gap-1"><strong className="font-semibold text-gray-700">Pending:</strong> {pendingCount}</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canMarkPostPlacement && (
              <button
                onClick={handleMarkPostPlacement}
                disabled={markingPostPlacement}
                className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  markingPostPlacement
                    ? 'cursor-not-allowed bg-amber-300 focus:ring-amber-200'
                    : 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500'
                }`}
              >
                {markingPostPlacement ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Info className="mr-2 h-4 w-4" />
                )}
                Mark Post-Placement
              </button>
            )}
            <button
              onClick={() => handleExportBatch(batch.batch_id, batch.batch_code)}
              className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => loadBatchDetail(batch.batch_id)}
              className="inline-flex items-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </button>
            <button
              onClick={handleSavePlacements}
              disabled={saving || !diff.changed || !canEditBatch}
              className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                saving || !diff.changed || !canEditBatch
                  ? 'bg-primary-300 cursor-not-allowed focus:ring-primary-200'
                  : 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-600'
              }`}
            >
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Changes
            </button>
          </div>
        </div>

        {!canEditBatch && (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            This batch is read-only because it is marked as {batch.status}. You can review existing records but cannot modify them.
          </div>
        )}

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {success}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Placement Progress</h3>
              {batch.ready_for_placements ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                  <Info className="h-3.5 w-3.5" aria-hidden="true" /> Ready for placements
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  {/* <Info className="h-3.5 w-3.5" aria-hidden="true" /> Awaiting post assessment */}
                </span>
              )}
            </div>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm text-gray-700">
              <div>
                <dt className="font-medium text-gray-600">Placed</dt>
                <dd>{recordedCount}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Pending</dt>
                <dd>{pendingCount}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Eligible</dt>
                <dd>{eligibleCount}</dd>
              </div>
              <div>
                <dt className="font-medium text-gray-600">Not Eligible</dt>
                <dd>{notEligibleCount}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">Bulk Fill Helpers</h3>
              <button
                onClick={handleResetBulkValues}
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
              >
                Reset
              </button>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input
                type="text"
                value={bulkValues.company_name}
                onChange={(e) => handleBulkValueChange('company_name', e.target.value)}
                placeholder="Company name"
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <input
                type="text"
                value={bulkValues.job_role}
                onChange={(e) => handleBulkValueChange('job_role', e.target.value)}
                placeholder="Job role"
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <select
                value={bulkValues.job_type}
                onChange={(e) => handleBulkValueChange('job_type', e.target.value)}
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="">Job type</option>
                {JOB_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={bulkValues.offered_salary}
                onChange={(e) => handleBulkValueChange('offered_salary', e.target.value)}
                placeholder="Salary"
                min="0"
                step="0.01"
                className="w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                onClick={handleApplyBulkValues}
                disabled={!canEditBatch}
                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  canEditBatch
                    ? 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-600'
                    : 'cursor-not-allowed bg-gray-200 text-gray-500 focus:ring-gray-200'
                }`}
              >
                <Copy className="h-4 w-4" /> Apply to pending trainees
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Applies to trainees who have completed their post assessment and do not yet have a placement recorded.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {tabConfig.map(({ key, label, count }) => {
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? 'border-primary-600 bg-primary-600 text-white shadow-sm'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>{label}</span>
                  <span
                    className={`inline-flex min-w-[1.75rem] items-center justify-center rounded-full border px-1 text-xs ${
                      isActive
                        ? 'border-white bg-white/20 text-white'
                        : 'border-gray-200 bg-gray-100 text-gray-600'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          {/* <button
            onClick={handleMarkRemainingLater}
            disabled={!canEditBatch}
            className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${
              canEditBatch
                ? 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
            }`}
          >
            <Clock3 className="h-4 w-4" /> Mark remaining as in-progress later
          </button> */}
        </div>

        {detailLoading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-primary-600">
            <div className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading batch placements...
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-max w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[160px]">Trainee</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 max-w-[180px]">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[160px]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[220px]">Company</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[220px]">Job Role</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[150px]">Job Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 min-w-[150px]">Salary (INR)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 min-w-[120px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTrainees.map((trainee) => {
                    const form = formState[trainee.trainee_id] ?? EMPTY_FORM;
                    const isEligible = trainee.post_assessment_done;
                    const isRecorded = trainee.has_placement;
                    const markedLater = skippedTrainees.has(trainee.trainee_id);
                    const canEditRow = canEditBatch && isEligible;

                    let rowClassName = 'transition-colors';
                    if (!isEligible) {
                      rowClassName += ' bg-gray-50 text-gray-500';
                    } else if (markedLater) {
                      rowClassName += ' bg-amber-50';
                    } else if (isRecorded) {
                      rowClassName += ' bg-green-50';
                    } else {
                      rowClassName += ' hover:bg-gray-50';
                    }

                    const inputClasses = `w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                      !canEditRow ? 'bg-gray-100 text-gray-500' : ''
                    }`;

                    const selectClasses = `w-full rounded-md border px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 ${
                      !canEditRow ? 'bg-gray-100 text-gray-500' : ''
                    }`;

                    const canMarkLater = canEditBatch && isEligible && !isRecorded;
                    const hadPlacement = originalState[trainee.trainee_id]?.hasPlacement ?? false;
                    const canClear = canEditBatch && (isEligible || hadPlacement);

                    return (
                      <tr key={trainee.trainee_id} className={rowClassName}>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-gray-900">{trainee.name}</div>
                          <div className="text-xs text-gray-500">ID: {trainee.trainee_id}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[180px] truncate">
                          <div className="truncate" title={trainee.email || ''}>{trainee.email || '—'}</div>
                          <div className="text-xs text-gray-500 truncate">{trainee.phone || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <div className="flex flex-col gap-1">
                            {isEligible ? (
                              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                Post assessment completed
                              </span>
                            ) : (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Post assessment pending
                              </span>
                            )}
                            {isRecorded && (
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                Placement recorded
                              </span>
                            )}
                            {markedLater && (
                              <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                Marked for later
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <input
                            type="text"
                            value={form.company_name}
                            onChange={(e) => handleFieldChange(trainee.trainee_id, 'company_name', e.target.value)}
                            disabled={!canEditRow}
                            className={inputClasses}
                            placeholder="Company name"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <input
                            type="text"
                            value={form.job_role}
                            onChange={(e) => handleFieldChange(trainee.trainee_id, 'job_role', e.target.value)}
                            disabled={!canEditRow}
                            className={inputClasses}
                            placeholder="Job role"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <select
                            value={form.job_type}
                            onChange={(e) => handleFieldChange(trainee.trainee_id, 'job_type', e.target.value)}
                            disabled={!canEditRow}
                            className={selectClasses}
                          >
                            <option value="">Select</option>
                            {JOB_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          <input
                            type="number"
                            value={form.offered_salary}
                            onChange={(e) => handleFieldChange(trainee.trainee_id, 'offered_salary', e.target.value)}
                            disabled={!canEditRow}
                            className={inputClasses}
                            placeholder="e.g. 25000"
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-gray-700">
                          <div className="flex flex-col items-end gap-2">
                            {/* <button
                              onClick={() => handleToggleSkip(trainee.trainee_id)}
                              disabled={!canMarkLater}
                              className={`inline-flex items-center rounded-md border px-3 py-1 text-xs ${
                                !canMarkLater
                                  ? 'cursor-not-allowed border-gray-200 text-gray-400'
                                  : markedLater
                                  ? 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {markedLater ? 'Unmark' : 'Mark later'}
                            </button> */}
                            <button
                              onClick={() => handleClearRow(trainee.trainee_id)}
                              disabled={!canClear}
                              className={`inline-flex items-center rounded-md border px-3 py-1 text-xs ${
                                !canClear
                                  ? 'cursor-not-allowed border-gray-200 text-gray-400'
                                  : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              Clear
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {filteredTrainees.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-sm text-gray-500">
                        {emptyStateMessage}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };
  const renderTraineeView = () => (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">My Placements</h1>
        <p className="text-sm text-gray-600">
          If data is incorrect, please connect with Bajaj Team on <a className="text-primary-600" href="mailto:xxxx@bajajfinserv.in">xxxx@bajajfinserv.in</a>.
        </p>
      </div>

      {traineeError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {traineeError}
        </div>
      )}

      <div className="bg-white border rounded-lg shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Batch</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Placement Details</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Recorded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {traineeLoading && (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-sm text-primary-600">
                    <div className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading placements...
                    </div>
                  </td>
                </tr>
              )}

              {!traineeLoading && traineePlacements.map((placement) => (
                <tr key={placement.placement_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="font-medium text-gray-900">{placement.batch_code || placement.batch_id}</div>
                    <div className="text-xs text-gray-500">Status: {placement.batch_status || '—'}</div>
                    <div className="text-xs text-gray-500">Program: {placement.program_name || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="font-medium text-gray-900">{placement.company_name || 'Company not recorded yet'}</div>
                    <div className="text-xs text-gray-500">Role: {placement.job_role || '—'}</div>
                    <div className="text-xs text-gray-500">Job Type: {placement.job_type || '—'}</div>
                    <div className="text-xs text-gray-500">Salary: {placement.offered_salary || '—'}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div className="text-xs text-gray-500">{placement.recorded_on ? new Date(placement.recorded_on).toLocaleString() : '—'}</div>
                    <div className="text-xs text-gray-500">by {placement.recorded_by || '—'}</div>
                  </td>
                </tr>
              ))}

              {!traineeLoading && traineePlacements.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-sm text-gray-500">
                    Placement information will appear here once recorded by your center admin.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
  if (!canManage && !isTrainee) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          You do not have access to view placement records.
        </div>
      </div>
    );
  }

  if (isTrainee && !canManage) {
    return (
      <div className="bg-gray-100 min-h-screen p-4 sm:p-6 md:p-8">
        <div className="max-w-7xl mx-auto">
          {renderTraineeView()}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {selectedDetail ? renderBatchDetail() : renderBatchList()}
      </div>
    </div>
  );
}

